/**
 * @file React hook — useStateDropdown.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import { DropdownOption } from "../interfaces/dropdownInterfaces";

const extractDropdownOptions = (payload: unknown): DropdownOption[] => {
  if (!payload) return [];
  const p = payload as Record<string, unknown>;
  if (Array.isArray(payload)) return payload as DropdownOption[];
  if (Array.isArray(p.options)) return p.options as DropdownOption[];
  if (Array.isArray((p.data as Record<string, unknown>)?.options)) {
    return (p.data as { options: DropdownOption[] }).options;
  }
  if (Array.isArray(p.data)) return p.data as DropdownOption[];
  return [];
};

/** Indian states dropdown (CMS dropdown id 1). */
export const useStateDropdown = () => {
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDropdown = useCallback(async () => {
    try {
      const res = await axiosClient.get("/dropdowns/1");
      setStateOptions(extractDropdownOptions(res.data));
      setError(null);
    } catch (err) {
      console.error("Failed to load state dropdown options", err);
      setStateOptions([]);
      setError("Failed to load state options");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDropdown();
  }, [loadDropdown]);

  return {
    stateOptions,
    loading,
    error,
    refetch: loadDropdown,
  };
};
